import { useCallback, useState, useEffect } from 'react';

import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { createContainer, useContainer } from 'unstated-next';

import { GET_USER_DATA } from '../../../../../config/apiPaths';
import { getUrlParams, getUrls, processApiPath } from '../../../../../utils/url';
import AdminAuthContainer from '../../../AdminAuthContainer';

interface SettingsContainerState {
    settingsData?: Record<string, any>; // result of the GET_CLASS_DATA_PATH api call
}

type LoadingStatus = 'INITIAL' | 'LOADING' | 'SUCCESS' | 'ERROR';

const useSettingsContainer = (state: SettingsContainerState = {}) => {
    console.log('SettingsContainer');
    const { api } = getUrls();

    const [settingsData, setSettingsData] = useState<any>();

    const { t: tCommon } = useTranslation('common');

    const { authMode } = useContainer(AdminAuthContainer);

    const search = new URLSearchParams(window.location.search);
    const settingsUuid = search.get('settings_uuid') || window.localStorage.getItem('settings_uuid');

    const [getSettingsStatus, setGetSettingsStatus] = useState<LoadingStatus>('INITIAL');
    const [errorMessage, setErrorMessage] = useState<string>('');

    const { settingsId: settingsIdV1 } = useParams<{ settingsId?: string }>();
    const { settings_id: settingsIdV2 } = getUrlParams();
    const settingsId = settingsIdV1 || settingsIdV2 || '';

    const returnErrorMessage = useCallback(
        (error: any): string => {
            console.error(error);
            return tCommon('errorMessage');
        },
        [tCommon]
    );

    const getSettingsDataCall = useCallback(async () => {
        const { data } = await axios.get(
            processApiPath(GET_USER_DATA, undefined, {
                settings_uuid: settingsUuid!,
                settings_id: settingsId,
                auth_mode: authMode
            })
        );

        if (data?.success === false) {
            throw Error(data?.error);
        }
        return data;
    }, [settingsUuid, settingsId, authMode]);

    const getSettings = useCallback(async () => {
        setGetSettingsStatus('LOADING');

        try {
            const data = state.settingsData ?? (await getSettingsDataCall());

            setSettingsData(data);
            setGetSettingsStatus('SUCCESS');
        } catch (err) {
            const errorMsg = returnErrorMessage(err);
            setErrorMessage(errorMsg);
            setGetSettingsStatus('ERROR');
        }
    }, [state.settingsData, getSettingsDataCall, returnErrorMessage]);

    useEffect(() => {
        if (settingsId && settingsUuid) {
            getSettings();
        }
    }, [settingsId, getSettings, settingsUuid]);

    return {
        settingsId,
        api,
        settingsData,
        getSettingsStatus,
        errorMessage
    };
};

const SettingsContainer = createContainer(useSettingsContainer);

export default SettingsContainer;
